import { useState } from "react";
import { List, Avatar, Input, Button, Upload, message } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { CommentProps } from "../../types/interfaces";
import { apiDownload, ApiError } from "../../hooks/apiRequest";
import { useAddComment } from "../../queries/tickets";
import { triggerBrowserDownload } from "../../utils/download";
import { ATTACHMENT_ACCEPT, validateAttachment } from "../../utils/attachments";
import styles from "./ticketThread.module.css";

export const CommentThread = ({ ticketId, comments }: { ticketId: string; comments: CommentProps[] }) => {
  const [text, setText] = useState("");
  const [attachment, setAttachment] = useState<File | null>(null);
  const addComment = useAddComment(ticketId);

  const handleDownload = async (commentId: string) => {
    try {
      const { blob, filename } = await apiDownload(`files/${commentId}`);
      triggerBrowserDownload(blob, filename);
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : "Could not download the attachment");
    }
  };

  const handleSubmit = async () => {
    if (!text.trim()) return;
    try {
      await addComment.mutateAsync({ comment: text.trim(), attachment });
      setText("");
      setAttachment(null);
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : "Could not add the comment");
    }
  };

  return (
    <div className={styles.thread}>
      <List<CommentProps>
        dataSource={comments}
        renderItem={(comment) => (
          <List.Item>
            <List.Item.Meta
              avatar={<Avatar>{comment.isAdmin ? "S" : "C"}</Avatar>}
              title={dayjs(comment.createdAt).format("YYYY-MM-DD HH:mm")}
              description={
                <>
                  <div>{comment.comment}</div>
                  {comment.hasAttachment && (
                    <Button type="link" size="small" onClick={() => handleDownload(comment._id)}>
                      Download attachment
                    </Button>
                  )}
                </>
              }
            />
          </List.Item>
        )}
      />
      <div className={styles.composer}>
        <Input.TextArea
          rows={2}
          value={text}
          placeholder="Add a comment…"
          onChange={(e) => setText(e.target.value)}
        />
        <Upload
          accept={ATTACHMENT_ACCEPT}
          beforeUpload={(file) => {
            const problem = validateAttachment(file);
            if (problem) {
              message.error(problem);
              return Upload.LIST_IGNORE;
            }
            setAttachment(file);
            return false;
          }}
          maxCount={1}
          showUploadList={!!attachment}
          onRemove={() => { setAttachment(null); return true; }}
        >
          <Button icon={<UploadOutlined />} aria-label="Attach a file" />
        </Upload>
        <Button type="primary" onClick={handleSubmit} loading={addComment.isPending}>
          Add comment
        </Button>
      </div>
    </div>
  );
};
